import React from 'react';
import { ChatMessageSendEvent } from '@progress/kendo-react-conversational-ui';

import {
  PostChat,
  PostTeamChat,
  getTeamSelectedAsync
} from '../../services/teams';
import {
  ChatBetweenPlayer,
  ChatToFacilitator,
  PostChatBetweenPlayer,
  PostChatFacilitator
} from '../../services/players';
import MobileTab from './mobile-tab';
import CommonTab from './common-tab';
import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../contexts/player-chat';
import { TeamChat } from './room/team-chat';
import { GroupChat } from './room/group-chat';
import { DirectChat } from './room/direct-chat';
import { PlayerState } from '../../types/state';
import { GameContext } from '../../contexts/game';
import { TeamContext } from '../../contexts/team';
import { PostGroupChat } from '../../services/groups';
import { PlayerContext } from '../../contexts/player';
import { AllPlayersChat } from './room/all-players-chat';
import { FacilitatorChat } from './room/facilitator-chat';
import { GameChat, PostGameChat } from '../../services/games';
import { GameDocumentContext } from '../../contexts/game-document';
import { ShowGameChat, ShowTeamChat } from '../../utils/game-engine/player';
import { GetTeamSelectedResponse } from '../../types/responses/team-response';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';

const ChatWindow = () => {
  const { setNewChat } = React.useContext(
    PlayerChatContext
  ) as PlayerChatContextProps;
  const [gameDocumentstate] = React.useContext(GameDocumentContext);
  const [gameState] = React.useContext(GameContext);
  const [playerState] = React.useContext(PlayerContext);
  const [teamState] = React.useContext(TeamContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);

  const [visibleButtonChat, setVisibleButtonChat] =
    React.useState<boolean>(true);
  const [allPlayersGroup, setAllPlayersGroup] = React.useState<boolean>(true);
  const [activeTab, setActiveTab] = React.useState<string>('');
  const [selectedPlayer, setSelectedPlayer] = React.useState<string>('');
  const [otherPlayers, setOtherPlayers] = React.useState<PlayerState[]>([]);

  const isInLobby = ['waiting', 'ready'].includes(
    playerState.playerState?.status ?? ''
  );
  const preGameSetting = gameDocumentstate.gameDocument?.settings.preGame;
  const inGameSetting =
    gameState.gameState?.runtime?.inGame ??
    gameDocumentstate.gameDocument?.settings.inGame;

  const showAllPlayerChat = isInLobby
    ? preGameSetting?.showPreGameAllPlayerChat
    : inGameSetting?.showInGameAllPlayerChat;
  const showFacilitatorChat = isInLobby
    ? preGameSetting?.showPreGameFacilitatorChat
    : inGameSetting?.showInGameFacilitatorChat;
  const showDirectChat = isInLobby
    ? preGameSetting?.showPreGameDirectChat
    : inGameSetting?.showInGameDirectChat;
  const showTeamChat = isInLobby
    ? preGameSetting?.showPreGameTeamChat
    : inGameSetting?.showInGameTeamChat;
  const showGroupChat = isInLobby
    ? preGameSetting?.showPreGameGroupChat
    : inGameSetting?.showInGameGroupChat;

  const teamCode = playerState.playerState?.teamCode;
  const groupCode =
    teamState.teamState?.groupCode !== '00000000-0000-0000-0000-000000000000'
      ? teamState.teamState?.groupCode
      : undefined;

  React.useEffect(() => {
    const chatRooms = [
      { enable: showAllPlayerChat, room: 'All Player' },
      { enable: showFacilitatorChat, room: 'Facilitators' },
      { enable: showDirectChat && otherPlayers.length > 0, room: 'Direct' },
      { enable: showTeamChat && teamCode !== undefined, room: 'Team' },
      { enable: showGroupChat && groupCode !== undefined, room: 'Group' }
    ];

    const firstActiveRoom = chatRooms.find((r) => r.enable === true);
    if (firstActiveRoom) setActiveTab(firstActiveRoom.room);
  }, [
    teamCode,
    groupCode,
    showTeamChat,
    showGroupChat,
    showDirectChat,
    showAllPlayerChat,
    showFacilitatorChat,
    otherPlayers.length
  ]);

  const addNewMessage = async (event: ChatMessageSendEvent) => {
    if (!visibleButtonChat) return;
    try {
      if (activeTab === 'All Player') {
        if (gameState?.gameCode) {
          let postChat: GameChat = {
            playerCode: playerState?.playerState?.code ?? '',
            gameCode: playerState?.gameCode!,
            message: event.message.text ?? ''
          };
          await PostGameChat(gameState?.gameCode, postChat);
          setNewChat((prev) => prev.filter((c) => c.group !== 'global-chat'));
        }
      } else if (activeTab === 'Team') {
        if (
          playerState?.playerState?.teamCode &&
          playerState?.playerState?.teamCode !== '' &&
          playerState?.playerState?.teamCode !==
            '00000000-0000-0000-0000-000000000000'
        ) {
          let postChat: PostChat = {
            playerCode: playerState?.playerState?.code ?? '',
            teamCode: playerState?.playerState?.teamCode,
            message: event.message.text ?? '',
            sentDateUtc: new Date().toISOString()
          };
          await PostTeamChat(
            playerState?.playerState?.teamCode,
            gameState.gameCode!,
            postChat
          );
          setNewChat((prev) => prev.filter((c) => c.group !== 'team-chat'));
        }
      } else if (activeTab === 'Group') {
        if (
          playerState?.playerState?.teamCode &&
          playerState?.playerState?.teamCode !== '' &&
          playerState?.playerState?.teamCode !==
            '00000000-0000-0000-0000-000000000000'
        ) {
          const teamState = (await getTeamSelectedAsync(
            gameState.gameCode!,
            playerState?.playerState?.teamCode
          )) as GetTeamSelectedResponse;

          let postChat: PostChat = {
            playerCode: playerState?.playerState?.code ?? '',
            teamCode: playerState?.playerState?.teamCode,
            message: event.message.text ?? '',
            sentDateUtc: new Date().toISOString()
          };
          await PostGroupChat(
            teamState.groupCode!,
            gameState.gameCode!,
            postChat
          );
          setNewChat((prev) => prev.filter((c) => c.group !== 'group-chat'));
        }
      } else if (activeTab === 'Facilitators') {
        let payload: ChatToFacilitator = {
          fromPlayerCode: playerState?.playerState?.code ?? '',
          message: event.message.text ?? ''
        };

        await PostChatFacilitator(gameState.gameCode!, payload);
        setNewChat((prev) =>
          prev.filter((c) => c.group !== 'facilitator-chat')
        );
      } else if (activeTab === 'Direct') {
        let payload: ChatBetweenPlayer = {
          fromPlayerCode: playerState?.playerState!.code!,
          toPlayerCode: selectedPlayer,
          message: event.message.text ?? ''
        };

        await PostChatBetweenPlayer(gameState?.gameCode!, payload);
        setNewChat((prev) =>
          prev.filter(
            (c) =>
              !(c.group === 'player-chat' && c.author.id === selectedPlayer)
          )
        );
      }
    } catch (error) {
      console.log('There is an error when sending chat: ', error);
    }
  };

  const directPlayerChat = (playerCode: string) => {
    setActiveTab('Direct');
    setSelectedPlayer(playerCode!);
  };

  const handleSwitchToDirectChat = () => {
    let firstPlayerCode = '';
    if (otherPlayers.length > 0) {
      firstPlayerCode = otherPlayers[0].code!;
    }
    directPlayerChat(firstPlayerCode!);
  };

  const handleSwitch = (chatType: string) => {
    setActiveTab(chatType);

    if (chatType === 'All Player') {
      setNewChat((prev) => prev.filter((c) => c.group !== 'global-chat'));
    } else if (chatType === 'Team') {
      setNewChat((prev) => prev.filter((c) => c.group !== 'team-chat'));
    } else if (chatType === 'Group') {
      setNewChat((prev) => prev.filter((c) => c.group !== 'group-chat'));
    } else if (chatType === 'Facilitators') {
      setNewChat((prev) => prev.filter((c) => c.group !== 'facilitator-chat'));
    }
  };

  const onChangePlayer = async (code: string) => {
    setSelectedPlayer(code);
    setNewChat((prev) =>
      prev.filter((c) => !(c.group === 'player-chat' && c.author.id === code))
    );
  };

  const handleChatVisibility = () => {
    if (allPlayersGroup) {
      let visible = ShowGameChat(
        playerState?.playerState!,
        gameDocumentstate?.gameDocument!
      );
      setVisibleButtonChat(visible!);
    } else {
      let visible = ShowTeamChat(
        playerState?.playerState!,
        gameDocumentstate?.gameDocument!
      );
      setVisibleButtonChat(visible!);
    }
  };

  const handleEnabledDisabledTeamChat = () => {
    setVisibleButtonChat(!teamState.teamState?.isChatDisabled!);
  };

  const getOtherPlayer = () => {
    const otherPlayers =
      gameState.gameState?.players
        .map((item) => {
          if (item.code !== playerState.playerState?.code) {
            return item;
          }

          return undefined;
        })
        .filter((player): player is PlayerState => !!player)
        .sort((a, b) => a.code!.localeCompare(b.code!)) || [];

    setOtherPlayers(otherPlayers);
  };

  const chatTypeText = React.useCallback(
    (type: string) => {
      switch (type) {
        case 'All Player':
          return (
            generateTitleById(
              'bd2f061c-b665-4fe5-84ce-b986a1008a76',
              gameDocumentstate,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'All players'
          );

        case 'Team':
          return (
            generateTitleById(
              '6a6f31b6-fb91-42f8-acab-f469e0ef1d86',
              gameDocumentstate,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'My team'
          );

        case 'Group':
          return (
            generateTitleById(
              '8fd3fb0f-e237-4717-9272-98a84f4e9af4',
              gameDocumentstate,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'My group'
          );

        case 'Facilitators':
          return (
            generateTitleById(
              'fe72a8db-b666-4720-b08c-6081605cd24c',
              gameDocumentstate,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Facilitators'
          );

        default:
          return 'Player chat';
      }
    },
    [
      gameDocumentstate,
      displayLanguageContext.displayLanguageSelected.resources
    ]
  );

  React.useEffect(() => {
    handleChatVisibility();
  }, [playerState?.playerState?.status]);

  React.useEffect(() => {
    handleEnabledDisabledTeamChat();
  }, [teamState.teamState?.isChatDisabled]);

  React.useEffect(() => {
    getOtherPlayer();
  }, [gameState.gameState?.players]);

  return (
    <div
      className={'d-flex flex-column flex-grow-1 h-100 w-100 overflow-hidden'}>
      <CommonTab
        activeTab={activeTab}
        otherPlayers={otherPlayers}
        chatTypeText={chatTypeText}
        handleSwitch={handleSwitch}
        handleSwitchToDirectChat={handleSwitchToDirectChat}
      />
      <MobileTab
        activeTab={activeTab}
        otherPlayers={otherPlayers}
        selectedPlayer={selectedPlayer}
        chatTypeText={chatTypeText}
        handleSwitch={handleSwitch}
        onChangePlayer={onChangePlayer}
        handleSwitchToDirectChat={handleSwitchToDirectChat}
      />

      <div className="d-flex w-100 flex-grow-1 h-100 overflow-hidden">
        {activeTab === 'All Player' && (
          <AllPlayersChat
            addNewMessage={addNewMessage}
            visibleButtonChat={visibleButtonChat}
          />
        )}

        {activeTab === 'Group' && (
          <GroupChat
            addNewMessage={addNewMessage}
            visibleButtonChat={visibleButtonChat}
          />
        )}

        {activeTab === 'Team' && (
          <TeamChat
            addNewMessage={addNewMessage}
            visibleButtonChat={visibleButtonChat}
          />
        )}

        {activeTab === 'Direct' && (
          <DirectChat
            playerList={otherPlayers}
            selectedPlayer={selectedPlayer}
            visibleButtonChat={visibleButtonChat}
            addNewMessage={addNewMessage}
            onChangePlayer={onChangePlayer}
          />
        )}

        {activeTab === 'Facilitators' && (
          <FacilitatorChat
            addNewMessage={addNewMessage}
            visibleButtonChat={visibleButtonChat}
          />
        )}
      </div>
    </div>
  );
};

export default ChatWindow;
